import {
	ArrowUpwardOutlined,
	ErrorOutlineOutlined,
	HelpOutlineOutlined,
	ReportProblemOutlined,
	VerifiedUserOutlined,
} from '@mui/icons-material'
import {
	Box,
	Checkbox,
	FormControlLabel,
	TextField,
	Theme,
	Typography,
} from '@mui/material'
import { amber, blue, green, red } from '@mui/material/colors'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import { action } from 'mobx'
import { observer } from 'mobx-react'
import React, {
	ComponentProps,
	FocusEventHandler,
	createRef,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react'
import { FullscreenSpinner } from '../../../../components/feedback/circular'
import { FixedHeaderWithHint } from '../../../../utils/HOC/FixedHeaders'
import {
	PropsEditorComponent,
	ValuesEditorComponent,
	specifyValueTypeComponent,
} from '../../../FormBuilderCore/cells/ComponentSpecification'
import { useFormBuilderContext } from '../../../FormBuilderCore/cells/rendering/contexts/FormBuilderContext'
import { useInputCellContext } from '../../../FormBuilderCore/cells/rendering/contexts/InputContextProvider'
import { FieldType } from '../../../FormHost/Types/FieldType'
import {
	ShortcutHandler,
	makeShortcutListener,
} from '../../../Ocr/KeyboardShortcutHandler'
import {
	OcrResultFieldValue,
	OcrResultFieldValueType,
} from '../../../Ocr/OcrTypes'
import {
	CheckboxPropertyConfigurator,
	NumberPropertyConfigurator,
	PropertySchematicConfigurator,
	TextPropertyConfigurator,
} from '../../Configuration'
import { changeEventEmitter } from '../../EventBus/BuiltInEvents/ChangeEvent'
import { focusEventEmitter } from '../../EventBus/BuiltInEvents/FocusEvent'
import { queryEventEmitter } from '../../EventBus/BuiltInEvents/QueryEvent'

type OcrResultFieldProps = {
	label: string
	showConfidence: boolean
	threshold: number
}

enum ConfidenceLevel {
	Accepted,
	High,
	Medium,
	Low,
	Unknown,
}

const getConfidenceLevel = (
	confidence: number | undefined,
	threshold: number,
): ConfidenceLevel => {
	if (confidence === undefined) return ConfidenceLevel.Unknown
	if (confidence >= threshold || threshold === 0)
		return ConfidenceLevel.Accepted
	return ConfidenceLevel.Low
}

export const OcrResultField = observer((props: OcrResultFieldProps) => {
	const { cellManager } = useFormBuilderContext()
	const context = useInputCellContext<OcrResultFieldValue>()

	const elementRootRef = createRef<HTMLDivElement>()

	const styles = useStyles()

	const cssClasses = useMemo(
		() => context.cellInstance.properties.cssClasses,
		[context.cellInstance.properties.cssClasses],
	)

	console.log('class names:', cssClasses)

	const onChange = useCallback(
		(newValue: unknown) => {
			if (context.value === null)
				throw new Error('ocr result field value is somehow null')

			const newOcrValue: OcrResultFieldValue = {
				...context.value,
				value: newValue,
			}

			changeEventEmitter(
				cellManager.eventBus,
				{
					oldValue: context.value,
					newValue: newOcrValue,
				},
				{
					formHost: cellManager,
					elementTag: OcrResultFieldElementTag,
					definitionId: context.cellInstance.definitionId,
					instanceId: context.cellInstance.id,
				},
			)

			context.onChange(newOcrValue)
		},
		[context, cellManager],
	)

	const focusEventHandler = useCallback(
		(e: 'FocusGained' | 'FocusLost', evt: React.FocusEvent) => {
			focusEventEmitter(
				cellManager.eventBus,
				{
					focusType: e,
					relatedTargetId: (evt.relatedTarget as Element)?.id,
					relatedTargetNodeName: (evt.relatedTarget as Element)?.nodeName,
				},
				{
					formHost: cellManager,
					elementTag: OcrResultFieldElementTag,
					definitionId: context.cellInstance.definitionId,
					instanceId: context.cellInstance.id,
				},
			)
		},
		[context, cellManager],
	)

	const onFocus = useCallback(
		action<React.FocusEventHandler<HTMLInputElement | HTMLButtonElement>>(
			(e: React.FocusEvent<HTMLInputElement | HTMLButtonElement>) => {
				console.log('focus gained!')
				focusEventHandler('FocusGained', e)
			},
		),
		[focusEventHandler],
	)

	const onBlur = useCallback(
		action<React.FocusEventHandler<HTMLInputElement | HTMLButtonElement>>(
			(e: React.FocusEvent<HTMLInputElement | HTMLButtonElement>) => {
				console.log('focus lost!')
				focusEventHandler('FocusLost', e)

				if (context.value === null) throw new Error('')

				context.onChange({ ...context.value, confidence: 100, accepted: true })
			},
		),
		[focusEventHandler],
	)

	useEffect(() => {
		if (elementRootRef.current === null) return
		const handlers: ShortcutHandler[] = [
			{
				requireCtrlKey: true,
				keys: ['q'],
				handler: () => {
					queryEventEmitter(
						cellManager.eventBus,
						{
							fieldName: context.cellInstance.name,
						},
						{
							formHost: cellManager,
							elementTag: OcrResultFieldElementTag,
							definitionId: context.cellInstance.definitionId,
							instanceId: context.cellInstance.id,
						},
					)
				},
			},
		]

		const disposer = makeShortcutListener(elementRootRef.current, handlers)

		return () => {
			disposer()
		}
	}, [context, cellManager])

	if (context.value === null) return <FullscreenSpinner />

	return (
		<Box
			ref={elementRootRef}
			display="flex"
			width="100%"
			paddingX={1}
			justifySelf="flex-start"
			alignSelf="flex-start"
		>
			<Box display="flex" alignItems="center" width="100%">
				<FixedHeaderWithHint
					className={clsx(styles.root, cssClasses)}
					label={
						<Box
							display="flex"
							flexDirection="row"
							justifyContent="space-between"
							width="100%"
						>
							<Typography
								variant="subtitle1"
								color={
									(context.value.confidence !== undefined &&
										context.value.confidence >= props.threshold) ||
									props.threshold === 0 ||
									context.value.confidence === undefined
										? undefined
										: 'red'
								}
							>
								{props.label}
							</Typography>
							<Box display="flex" alignItems="center" gap={0.5}>
								<Typography variant="body1">
									{props.showConfidence &&
										context.value.confidence !== undefined &&
										`${context.value.confidence}%`}
								</Typography>
								<FieldConfidence
									confidence={context.value?.confidence}
									threshold={props.threshold}
								/>
							</Box>
						</Box>
					}
					// hint={props.hint} TODO!!!
				>
					<Box marginY={0.5}>
						<InnerOcrField
							classNames={cssClasses}
							value={context.value}
							cellInstanceId={context.cellInstance.id}
							onChange={onChange}
							onFocus={onFocus}
							onBlur={onBlur}
							valueType={context.value.valueType}
							accepted={
								(context.value.confidence !== undefined &&
									context.value.confidence >= props.threshold) ||
								props.threshold === 0
							}
						/>
					</Box>
				</FixedHeaderWithHint>
			</Box>
		</Box>
	)
})

type FieldConfidenceProps = {
	confidence?: number
	threshold: number
}

const FieldConfidence = (props: FieldConfidenceProps) => {
	const confidenceLevel = getConfidenceLevel(props.confidence, props.threshold)

	return (
		<>
			{confidenceLevel == ConfidenceLevel.Accepted && (
				<VerifiedUserOutlined htmlColor={green[600]} />
			)}
			{confidenceLevel == ConfidenceLevel.High && (
				<ArrowUpwardOutlined htmlColor={green[600]} />
			)}
			{confidenceLevel == ConfidenceLevel.Medium && (
				<ReportProblemOutlined htmlColor={amber[500]} />
			)}
			{confidenceLevel == ConfidenceLevel.Low && (
				<ErrorOutlineOutlined htmlColor={red[500]} />
			)}
			{confidenceLevel == ConfidenceLevel.Unknown && (
				<HelpOutlineOutlined htmlColor={blue[600]} />
			)}
		</>
	)
}

type InnerOcrFieldProps = InputOcrFieldProps | ButtonOcrFieldProps

type InputOcrFieldProps = {
	valueType:
		| OcrResultFieldValueType.String
		| OcrResultFieldValueType.Number
		| OcrResultFieldValueType.StringArray
	onFocus: FocusEventHandler<HTMLInputElement>
	onBlur: FocusEventHandler<HTMLInputElement>
	cellInstanceId: string
	value: OcrResultFieldValue
	hint?: string
	onChange: (newValue: unknown) => void
	accepted: boolean
	classNames?: string
}

type ButtonOcrFieldProps = {
	valueType: OcrResultFieldValueType.Boolean
	onFocus: FocusEventHandler<HTMLButtonElement>
	onBlur: FocusEventHandler<HTMLButtonElement>
	cellInstanceId: string
	value: OcrResultFieldValue
	onChange: (newValue: unknown) => void
	accepted: boolean
}

const InnerOcrField = (props: InnerOcrFieldProps) => {
	const styles = useStyles()

	console.log('inner ocr field props:', JSON.stringify(props, null, 2))

	const [showHint, setShowHint] = useState(false)

	if (props.valueType === OcrResultFieldValueType.String)
		return (
			<>
				<TextField
					value={props.value.value}
					fullWidth
					id={props.cellInstanceId}
					onChange={(evt) => props.onChange(evt.target.value)}
					onFocus={(evt) => {
						props.onFocus(evt as React.FocusEvent<HTMLInputElement>)
						setShowHint(true)
					}}
					onBlur={props.onBlur}
					sx={{
						fieldset: {
							borderColor:
								props.accepted || props.value.confidence === undefined
									? ''
									: red[500],
							// bgcolor: props.value.accepted ? '' : red[100],
						},
					}}
					inputProps={{
						className: clsx(props.classNames),
					}}
					size="small"
				/>
				<Typography variant="subtitle2">
					{showHint && props.value.hintText}
				</Typography>
			</>
		)

	if (props.valueType === OcrResultFieldValueType.Number)
		return (
			<>
				<TextField
					type="number"
					fullWidth
					value={props.value.value}
					id={props.cellInstanceId}
					onChange={(evt) => props.onChange(evt.target.value)}
					onFocus={(evt) => {
						props.onFocus(evt as React.FocusEvent<HTMLInputElement>)
						setShowHint(true)
					}}
					onBlur={(evt) => {
						props.onBlur(evt as React.FocusEvent<HTMLInputElement>)
						setShowHint(false)
					}}
					sx={{
						fieldset: {
							borderColor: props.accepted ? '' : red[500],
							// bgcolor: props.value.accepted ? '' : red[100],
						},
					}}
					inputProps={{
						className: clsx(props.classNames),
					}}
				/>
				<Typography variant="subtitle2">
					{showHint && props.value.hintText}
				</Typography>
			</>
		)

	if (props.valueType === OcrResultFieldValueType.Boolean)
		return (
			<FormControlLabel
				control={
					<Checkbox
						id={props.cellInstanceId}
						onFocus={props.onFocus}
						onBlur={props.onBlur}
						checked={props.value.value as boolean}
						onChange={() => props.onChange(!props.value.value as boolean)}
						focusRipple={true}
						onFocusVisible={props.onFocus}
						focusVisibleClassName={styles.checkboxFocus}
						color={props.accepted ? 'primary' : 'error'}
					/>
				}
				label={props.value.hintText}
			/>
		)
	if (props.valueType === OcrResultFieldValueType.StringArray) {
		const array = props.value.value as string[]
		const stringValue = array.join('\n')
		return (
			<TextField
				id={props.cellInstanceId}
				multiline
				fullWidth
				value={stringValue}
				onFocus={props.onFocus}
				onBlur={props.onBlur}
				onChange={(evt) => props.onChange(evt.currentTarget.value.split('\n'))}
				sx={{
					fieldset: {
						borderColor: props.accepted ? '' : red[500],
						// bgcolor: props.value.accepted ? '' : red[100],
					},
				}}
				inputProps={{
					className: clsx(props.classNames),
				}}
			/>
		)
	}
	// if (props.valueType === OcrResultFieldValueType.StringArray) {
	// 	const array = props.value.value as string[]
	// 	return (
	// 		<Box display="flex" flexDirection="column" id={props.cellInstanceId}>
	// 			{array.map((v, i) => (
	// 				<TextField
	// 					key={i}
	// 					value={v}
	// 					onChange={(evt) => {
	// 						const newValue = [...array]
	// 						newValue[i] = evt.target.value
	// 						props.onChange(newValue)
	// 					}}
	// 				/>
	// 			))}
	// 		</Box>
	// 	)
	// }
	return <>No input field components were configured for this value type</>
}

export const OcrResultFieldElementTag = '50b6c95a-b711-48cc-8147-083327af7513'

export const OcrResultFieldId = 'd951e377-91c3-4b8f-a73d-a15c3dabff83'

const OcrResultFieldPropsEditor = ({
	properties,
	onPropertiesChanged,
}: PropsEditorComponent<ComponentProps<typeof OcrResultField>>) => {
	return (
		<>
			<PropertySchematicConfigurator
				value={properties.label}
				onValueChanged={(v) => onPropertiesChanged({ ...properties, label: v })}
				Editor={TextPropertyConfigurator}
				label="Label"
			/>
			<PropertySchematicConfigurator
				value={properties.threshold}
				onValueChanged={(v) =>
					onPropertiesChanged({ ...properties, threshold: v })
				}
				Editor={NumberPropertyConfigurator}
				label="Confidence Threshold"
			/>
			<PropertySchematicConfigurator
				value={properties.showConfidence}
				onValueChanged={(v) =>
					onPropertiesChanged({ ...properties, showConfidence: v })
				}
				Editor={CheckboxPropertyConfigurator}
				label="Show Confidence Value"
			/>
		</>
	)
}

const OcrResultFieldValueEditor = (
	props: ValuesEditorComponent<ComponentProps<typeof OcrResultField>>,
) => <></>

export const OcrResultFieldDefinition = specifyValueTypeComponent(
	OcrResultFieldId,
	OcrResultField,
	OcrResultFieldElementTag,
	'OCR Result Field',
	FieldType.ReferenceType,
	{
		configurator: OcrResultFieldPropsEditor,
		defaultProperties: {
			label: '',
			showConfidence: false,
			threshold: 80,
		},
	},
	{
		configurator: OcrResultFieldValueEditor,
		defaultValue: {
			value: '',
			accepted: true,
			confidence: undefined,
			valueType: OcrResultFieldValueType.String,
			boundingBox: [],
			pageNumber: 0,
			hintText: undefined,
		},
	},
)

const useStyles = makeStyles((theme: Theme) => ({
	root: {
		width: '100%',
	},
	checkboxFocus: {
		borderStyle: 'solid',
		borderColor: theme.palette.primary.main,
		borderWidth: '2px',
		borderRadius: theme.spacing(2),
		margin: theme.spacing(0.5),
	},
}))
